import { useEffect, useRef } from 'react';
import { Button } from '../../components/Button';
import type { OrganizationSampleDataStatus, SampleDataCounts } from '../../types/sampleData';
import { dateTimeFormatter } from '../../utils/dateTimeParts';
import './OrganizationSampleDataPanel.css';

const COUNT_LABELS: { key: keyof SampleDataCounts; label: string }[] = [
  { key: 'teams', label: 'Teams' },
  { key: 'prospects', label: 'Players' },
  { key: 'rosterBoards', label: 'Roster boards' },
  { key: 'rosterBoardPlayers', label: 'Board placements' },
  { key: 'notes', label: 'Notes' },
  { key: 'events', label: 'Events' },
  { key: 'calls', label: 'Calls' },
];

const formatSeededAt = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : dateTimeFormatter(undefined, { dateStyle: 'medium' }).format(date);
};

/** The "Try again" control on a failed calls line. */
export interface RetryCallsControl {
  onRetry: () => void;
  /** This retry is in flight. */
  running: boolean;
  /** Another panel action is in flight. */
  disabled: boolean;
}

/**
 * The button is disabled while its request runs, and a browser drops focus from a disabled element
 * to <body>. When the request settles with the button still here (a 409, or a retry that failed
 * again), focus goes back to it so a keyboard user can try again from where they were.
 */
const RetryCallsButton = ({ onRetry, running, disabled }: RetryCallsControl) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasRunningRef = useRef(running);

  useEffect(() => {
    const settled = wasRunningRef.current && !running;
    wasRunningRef.current = running;
    if (settled && (document.activeElement === document.body || document.activeElement === null)) {
      buttonRef.current?.focus();
    }
  }, [running]);

  return (
    <Button
      ref={buttonRef}
      variant="secondary"
      onClick={onRetry}
      disabled={disabled || running}
      aria-busy={running || undefined}
      aria-label="Try again adding sample calls"
      data-testid="sample-data-calls-retry"
    >
      Try again
    </Button>
  );
};

interface CallsStatusLineProps {
  status: OrganizationSampleDataStatus;
  retryCalls: RetryCallsControl;
}

/** Sample calls wait for the organization's first user, since a call needs a coach to belong to. */
const CallsStatusLine = ({ status, retryCalls }: CallsStatusLineProps) => {
  switch (status.callsStatus) {
    case 'Pending':
      return (
        <p className="org-sample-data__calls" data-testid="sample-data-calls-status">
          Sample calls are added when the first user is created.
        </p>
      );
    case 'Seeding':
      return (
        <p className="org-sample-data__calls" data-testid="sample-data-calls-status">
          Adding sample calls...
        </p>
      );
    case 'Failed':
      return (
        <p
          className="org-sample-data__calls org-sample-data__calls--failed"
          data-testid="sample-data-calls-status"
        >
          <span>
            {status.callsError
              ? `Adding sample calls failed: ${status.callsError}`
              : 'Adding sample calls failed.'}
          </span>
          <RetryCallsButton {...retryCalls} />
        </p>
      );
    default:
      return null;
  }
};

interface OrganizationSampleDataSummaryProps {
  status: OrganizationSampleDataStatus;
  retryCalls: RetryCallsControl;
}

/** What an active sample data set contains. */
export const OrganizationSampleDataSummary = ({
  status,
  retryCalls,
}: OrganizationSampleDataSummaryProps) => (
  <div className="org-sample-data__summary">
    {status.seededAt && (
      <p className="org-sample-data__meta">Added {formatSeededAt(status.seededAt)}</p>
    )}
    <dl className="org-sample-data__counts" data-testid="sample-data-counts">
      {COUNT_LABELS.map(({ key, label }) => (
        <div className="org-sample-data__count" key={key}>
          <dt className="org-sample-data__count-label">{label}</dt>
          <dd className="org-sample-data__count-value">{status.counts[key]}</dd>
        </div>
      ))}
    </dl>
    <CallsStatusLine status={status} retryCalls={retryCalls} />
  </div>
);
